import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Layout/Navbar';
import ProtectedRoute from './components/routing/ProtectedRoute';
import PublicRoute from './components/routing/PublicRoute';
import AdminRoute from './components/routing/AdminRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectVideosPage from './pages/ProjectVideosPage';
import AdminMetricsPage from './pages/AdminMetricsPage';
import VideoOverviewPage from './pages/VideoOverviewPage';
import ReviewQueuePage from './pages/ReviewQueuePage';
import ReportPage from './pages/ReportPage';
import ProjectLayout from './components/Layout/ProjectLayout';
import StandardsPage from './pages/StandardsPage';
import StandardDetailPage from './pages/StandardDetailPage';
import ImportStandardPage from './pages/ImportStandardPage';
import CriteriaPage from './pages/CriteriaPage';
import UsersPage from './pages/UsersPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// The project's Review and Reports pages were retired into its Videos list (RUNLOG V8). Their
// old addresses land on that list instead of the catch-all's global dashboard, so a bookmark
// or an old link still arrives somewhere that makes sense.
function ProjectVideosRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${projectId}`} replace />;
}

// Navbar/padding are gated on `user` (not just route) so /login and /register
// never render a nav bar with no signed-in user to greet or log out.
function AppShell() {
  const { user } = useAuth();

  return (
    // QA-36: print:bg-white for the same reason as ProjectLayout's root (see the comment
    // there) - this is the outermost painted box, so with Background graphics on it is the
    // one that tints the printed page off-white.
    <div className="min-h-screen bg-[#F8F8FF] print:bg-white">
      {user && <Navbar />}
      <main className={user ? 'pt-16 print:pt-0' : ''}>
        <div className="w-full min-w-0 max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* 19.5's route table: /standards and /standards/:standardId are ProtectedRoute
                (admin-only buttons gated inside the page by role, not the route); the write
                paths (/admin/standards/import, /criteria) are AdminRoute. The per-video
                routes under /projects/:projectId/videos/... remain P5-P7 work. */}
            <Route
              path="/standards"
              element={
                <ProtectedRoute>
                  <StandardsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/standards/:standardId"
              element={
                <ProtectedRoute>
                  <StandardDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/standards/import"
              element={
                <AdminRoute>
                  <ImportStandardPage />
                </AdminRoute>
              }
            />
            {/* P8: usage, cost, pipeline stages, review outcomes and the live log tail. */}
            <Route
              path="/admin/metrics"
              element={
                <AdminRoute>
                  <AdminMetricsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/criteria"
              element={
                <AdminRoute>
                  <CriteriaPage />
                </AdminRoute>
              }
            />
            {/* Anshul, 2026-09-26: admin-only auditor account management. */}
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <UsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <ProtectedRoute>
                  <ProjectLayout />
                </ProtectedRoute>
              }
            >
              {/* The videos list IS the project. A separate project Dashboard and Videos
                  page showed overlapping data, so every journey paid a "click Videos" tax,
                  and that dashboard also carried a stale "run history isn't available yet"
                  panel describing a feature that shipped in P5. The list absorbed its useful
                  half (the stat strip) and ProjectDashboardPage is gone. */}
              <Route index element={<ProjectVideosPage />} />
              {/* P6. ProjectLayout's Videos nav item and ProjectDashboardPage's upload
                  actions are gated on FEATURES.projectVideos, which is flipped in the
                  same commit as this route so a link can never point at a route that
                  does not exist. */}
              {/* Kept as an alias rather than removed: VideoHeaderStrip's "Back to
                  videos" link and any bookmark still point here, and a route that
                  silently bounces to the dashboard is exactly the dead control this
                  codebase keeps having to fix. Same component, so they cannot drift. */}
              <Route path="videos" element={<ProjectVideosPage />} />
              {/* P7, rebuilt in V8. The per-video pages render inside ProjectLayout with a
                  header strip and Overview / Review Queue / Report tabs. The project-level
                  Review and Reports indexes were retired into the Videos list (the
                  2026-09-25 brief, section 4); their paths redirect to it. */}
              <Route path="videos/:videoId" element={<VideoOverviewPage />} />
              <Route path="videos/:videoId/review" element={<ReviewQueuePage />} />
              <Route path="videos/:videoId/report" element={<ReportPage />} />
              <Route path="review" element={<ProjectVideosRedirect />} />
              <Route path="reports" element={<ProjectVideosRedirect />} />
            </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </main>
      <Toaster
        position="top-right"
        containerStyle={{ zIndex: 100000 }}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            theme: {
              primary: '#4aed88',
            },
          },
          error: {
            duration: 6000,
            theme: {
              primary: '#f56565',
            },
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
